import { Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { renderLayoutCalibrationHtml, type LabelLayout } from '@aeris/printing'
import { Button, Dialog, DialogContent, DialogFooter } from '@aeris/ui'

import { printHtmlDocument } from '../lib/label-print.ts'
import { formatDecimalInput } from '../lib/label-template-draft.ts'

/** Duas fileiras: a segunda diz se o papel anda o que deve. */
export const ROLL_TEST_ROWS = 2

/**
 * O Teste da bobina — F264.
 *
 * A bobina não imprime na folha de papel comum, como a folha de teste da F140:
 * o contorno de cada adesivo sai no próprio adesivo. E antes de imprimir há o
 * que nenhuma página consegue fazer sozinha — calibrar a impressora e criar no
 * driver um papel do tamanho de uma fileira. Por isso um diálogo, e não um
 * botão que imprime direto: os passos vêm com as medidas DESTE modelo, e o
 * "como ler" fica ao lado de quem segura o papel impresso.
 *
 * Foi a Zebra da Elite GV: a página do Aeris era 107 × 21, certa, e a etiqueta
 * saía 13 mm abaixo e cortada, porque o papel do driver era outro e o Chrome
 * encolhe e centraliza a página no papel que o driver tem.
 */
export const RollTestDialog = ({
  layout,
  onClose,
}: {
  layout: LabelLayout
  onClose: () => void
}): ReactNode => {
  const [error, setError] = useState<string | null>(null)
  const width = formatDecimalInput(layout.page.widthMm)
  const height = formatDecimalInput(layout.page.heightMm)
  const paper = `${width} × ${height} mm`

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title="Teste da bobina"
        description={`Duas fileiras com o contorno de cada adesivo, no papel de ${paper}.`}
        className="max-w-xl"
      >
        <div className="flex flex-col gap-4 text-(length:--text-sm)">
          <p className="rounded-(--radius-md) bg-(--surface-sunken) px-3 py-2">
            O papel da impressora é <strong>uma fileira</strong>:{' '}
            <strong className="text-(length:--text-md)">{paper}</strong>.
          </p>

          <section className="flex flex-col gap-1.5">
            <h3 className="font-(--weight-semibold)">
              Uma vez, na impressora e no Windows
            </h3>
            <ol className="list-decimal space-y-1 pl-5">
              <li>
                <strong>Calibre a impressora</strong> com a bobina colocada (na
                Zebra: segure PAUSE e CANCEL juntos por 2 segundos). Ela puxa
                algumas etiquetas e mede o espaço entre elas.
              </li>
              <li>
                Nas <strong>Preferências de impressão</strong> da impressora,
                crie um papel de <strong>{paper}</strong>, tipo etiqueta com
                espaço (gap), e deixe-o como padrão.
              </li>
              <li>
                No Chrome, em <strong>Mais definições</strong>: Tamanho do papel{' '}
                <strong>{paper}</strong>, Margens <strong>Nenhuma</strong>,
                Escala <strong>Padrão</strong> e sem Cabeçalhos e rodapés.
              </li>
            </ol>
          </section>

          <section className="flex flex-col gap-1.5">
            <h3 className="font-(--weight-semibold)">Como ler o teste</h3>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                Cada contorno inteiro dentro do seu adesivo, com a mesma folga
                nos quatro lados, e a barra com 10 mm numa régua:{' '}
                <strong>pronto</strong>, pode imprimir.
              </li>
              <li>
                Contorno menor, ou no meio do adesivo: o papel escolhido no
                Chrome não é {paper}.
              </li>
              <li>
                Desceu, subiu, cortou embaixo ou pulou adesivo: a altura do
                papel não é {height} mm, ou a impressora não foi calibrada.
              </li>
              <li>
                Todos deslocados para o mesmo lado: acerte a margem{' '}
                <strong>Esquerda</strong> deste modelo.
              </li>
            </ul>
          </section>

          {error === null ? null : (
            <p role="alert" className="text-(--danger)">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="primary"
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              setError(null)
              printHtmlDocument(
                renderLayoutCalibrationHtml(layout, { pages: ROLL_TEST_ROWS })
              ).catch((cause: unknown) => {
                setError(
                  cause instanceof Error
                    ? cause.message
                    : 'Não foi possível abrir a impressão.'
                )
              })
            }}
          >
            Imprimir {String(ROLL_TEST_ROWS)} fileiras de teste
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
